import * as React from 'react';
import { createElement } from 'react';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useLocalization } from '@shared-ui/localization-context';
import { Swipeable } from 'react-swipeable';
import { c as classNames, l as liveAnnounce, L as Level } from '../theme-provider/index-chunk.esm.js';
import { b as EGDSPagingButtonTheme } from '../paging-button/index-chunk.esm.js';
import { a as EGDSPagingPrevButton, E as EGDSPagingNextButton } from '../paging-prev-button/index-chunk.esm.js';
import { E as EGDSOverlayButton } from '../overlay-button/index-chunk.esm.js';
import { a as EGDSIcon } from '../icon/index-chunk.esm.js';
import { b as EGDSImage, a as EGDSFigure, c as EGDSImageLink, E as EGDSFigureAspectRatioType } from '../image-link/index-chunk.esm.js';
import { E as EGDSScrim, a as EGDSScrimType } from '../scrim/index-chunk.esm.js';
import { E as EGDSQueuedImage } from '../queued-image/index-chunk.esm.js';
import { i as isYoutubeSource, E as EGDSVideo } from '../video-layer-loading/index-chunk.esm.js';
import '../layout-position/index-chunk.esm.js';
import '../page-loader/index-chunk.esm.js';
import styles from '../styles/uitk-egds-gallery-carousel.css';

const getClassNameList$2 = classNames.bind(styles);
class EGDSGalleryCarouselItem extends React.PureComponent {
    static displayName = 'EGDSGalleryCarouselItem';
    static defaultProps = {
        ariaHidden: false,
    };
    render() {
        const { ariaHidden, className, alt, src, ratio, enablePreload, onClick, videoAnalytics, videoButtonText, placeholderImage, triggerA11yText, fetchPriority, triggerRef, } = this.props;
        const cssClassNames = getClassNameList$2(`uitk-gallery-carousel-item`, className);
        const wrappedClassName = getClassNameList$2(className);
        let renderedElement;
        if (wrappedClassName && wrappedClassName.includes('uitk-gallery-carousel-item-current')) {
            renderedElement = isYoutubeSource(src) ? (jsx(EGDSVideo, { alt: alt, analytics: videoAnalytics, src: src, buttonText: videoButtonText })) : (jsx(EGDSImage, { alt: alt, src: enablePreload ? src : '', placeholderImage: placeholderImage, additionalProps: { fetchPriority } }));
        }
        else {
            renderedElement = jsx(EGDSQueuedImage, { alt: alt, src: enablePreload ? src : '' });
        }
        return (jsx("div", { className: cssClassNames, "aria-hidden": ariaHidden, children: jsxs(EGDSFigure, { imageFit: "cover", ratio: ratio, children: [renderedElement, onClick && !isYoutubeSource(src) && (jsx(EGDSImageLink, { children: jsx("button", { type: "button", onClick: onClick, "data-testid": "uitk-gallery-item-current-trigger", "aria-label": triggerA11yText, ref: triggerRef, children: alt }) }))] }) }));
    }
}

const getClassNameList$1 = classNames.bind(styles);
const EGDSHeadingTagMap = [
    ['h1', 'h1'],
    ['h2', 'h2'],
    ['h3', 'h3'],
    ['h4', 'h4'],
    ['h5', 'h5'],
    ['h6', 'h6'],
];
const defaultHeadingTag = 'h3';
const EGDSHeadingTags = new Map(EGDSHeadingTagMap);
var EGDSGalleryCarouselLocalAnalyticsIdType;
(function (EGDSGalleryCarouselLocalAnalyticsIdType) {
    EGDSGalleryCarouselLocalAnalyticsIdType["clickLeft"] = "clickLeft";
    EGDSGalleryCarouselLocalAnalyticsIdType["clickRight"] = "clickRight";
})(EGDSGalleryCarouselLocalAnalyticsIdType || (EGDSGalleryCarouselLocalAnalyticsIdType = {}));
class GalleryCarousel extends React.Component {
    static defaultProps = {
        currentIndex: 0,
        enablePreload: true,
        navTheme: EGDSPagingButtonTheme.SOLID,
        ratio: EGDSFigureAspectRatioType.R16_9,
        showGalleryButton: true,
        showVirtualTourButton: false,
    };
    nextprevCallbackTimer = 250;
    _animationTimer;
    constructor(props) {
        super(props);
        this.state = {
            currentIndex: props.currentIndex || 0,
            enablePreload: Boolean(props.enablePreload),
            isInteractive: false,
        };
        this.onImageClickCallback = this.onImageClickCallback.bind(this);
        this.onNextCallback = this.onNextCallback.bind(this);
        this.onPrevCallback = this.onPrevCallback.bind(this);
        this.onUpdateCallback = this.onUpdateCallback.bind(this);
        this.onMediaButtonCallback = this.onMediaButtonCallback.bind(this);
        this.onVirtualTourCallback = this.onVirtualTourCallback.bind(this);
        this.getBackwardIndex = this.getBackwardIndex.bind(this);
        this.getForwardIndex = this.getForwardIndex.bind(this);
        this.onSwiping = this.onSwiping.bind(this);
    }
    componentDidMount() {
        this.setState({
            isAnimating: false,
            isInteractive: true,
        });
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        const nextCurrIndex = nextProps.currentIndex;
        const { currentIndex } = this.state;
        if (nextCurrIndex !== currentIndex) {
            this.setState({ currentIndex: nextCurrIndex });
        }
    }
    shouldComponentUpdate(nextProps, nextState) {
        const { currentIndex, images } = this.props;
        const { isAnimating } = this.state;
        const nextImages = nextProps.images;
        // Only re-render when images, isAnimating or currentIndex change
        const imageLength = images && images.length;
        const nextImageLength = nextImages && nextImages.length;
        if (imageLength !== nextImageLength) {
            return true;
        }
        if (isAnimating !== nextState.isAnimating) {
            return true;
        }
        if (currentIndex !== nextProps.currentIndex) {
            return true;
        }
        if (imageLength > 0 && nextImageLength > 0 && images[0].src !== nextImages[0].src) {
            return true;
        }
        return false;
    }
    onPrevCallback() {
        const { isAnimating } = this.state;
        const { images } = this.props;
        if (isAnimating) {
            return;
        }
        let newIndex = this.currentIndex - 1;
        const imagesLength = images.length;
        if (newIndex < 0) {
            newIndex = imagesLength - 1;
        }
        this.setState({
            enablePreload: true,
            isAnimating: true,
            isAnimatingNext: false,
            isAnimatingPrev: true,
            isInteractive: false,
        });
        if (this._animationTimer) {
            clearInterval(this._animationTimer);
        }
        this._animationTimer = setTimeout(() => {
            this.announce(newIndex);
            this.onUpdateCallback(newIndex);
        }, this.nextprevCallbackTimer);
    }
    onNextCallback() {
        const { isAnimating } = this.state;
        const { images } = this.props;
        if (isAnimating) {
            return;
        }
        let newIndex = this.currentIndex + 1;
        const imagesLength = images.length;
        if (newIndex + 1 > imagesLength) {
            newIndex = 0;
        }
        this.setState({
            enablePreload: true,
            isAnimating: true,
            isAnimatingNext: true,
            isAnimatingPrev: false,
            isInteractive: false,
        });
        if (this._animationTimer) {
            clearInterval(this._animationTimer);
        }
        this._animationTimer = setTimeout(() => {
            this.announce(newIndex);
            this.onUpdateCallback(newIndex);
        }, this.nextprevCallbackTimer);
    }
    onUpdateCallback(currentIndex) {
        const { onIndexChange } = this.props;
        if (typeof onIndexChange === 'function') {
            onIndexChange(currentIndex);
        }
    }
    onImageClickCallback() {
        const { onImageClick } = this.props;
        if (typeof onImageClick === 'function') {
            onImageClick();
        }
    }
    onMediaButtonCallback() {
        const { onMediaButtonClick } = this.props;
        if (typeof onMediaButtonClick === 'function') {
            onMediaButtonClick(this.currentIndex + 1);
        }
    }
    onVirtualTourCallback() {
        const { onVirtualTourClick } = this.props;
        if (typeof onVirtualTourClick === 'function') {
            onVirtualTourClick();
        }
    }
    onSwiping(event) {
        const { images } = this.props;
        const { isInteractive } = this.state;
        const imagesLength = images.length;
        const canSwipe = isInteractive && images && imagesLength > 1;
        if (canSwipe && event.dir === 'Left') {
            this.onNextCallback();
        }
        if (canSwipe && event.dir === 'Right') {
            this.onPrevCallback();
        }
    }
    getBackwardIndex(count) {
        const { images } = this.props;
        if (images && images.length > 0 && count > 0) {
            if (images.length === 1) {
                return 0;
            }
            const softIndex = this.currentIndex - count;
            if (softIndex >= 0) {
                return softIndex;
            }
            if (Math.abs(softIndex % images.length) === 0) {
                return 0;
            }
            return Math.abs(images.length - Math.abs(softIndex % images.length));
        }
        return 0;
    }
    getForwardIndex(count) {
        const { images } = this.props;
        if (images && images.length > 0 && count > 0) {
            const softIndex = this.currentIndex + count;
            return Math.abs(softIndex % images.length);
        }
        return this.currentIndex;
    }
    // Getter / setter for this.currentIndex
    get currentIndex() {
        const { currentIndex } = this.state;
        return currentIndex;
    }
    set currentIndex(value) {
        this.setState({ currentIndex: value });
    }
    stopTransitionPropagation(e) {
        e.stopPropagation();
    }
    announce(index) {
        const { images, l10n } = this.props;
        const { currentIndex } = this.state;
        let captionToBeRead;
        if (images[index].caption !== undefined) {
            captionToBeRead = images[index].caption;
        }
        else if (images[index].alt === undefined) {
            captionToBeRead = '';
        }
        else {
            captionToBeRead = images[index].alt;
        }
        this.setState({
            currentIndex: index,
            isAnimating: false,
            isAnimatingNext: false,
            isAnimatingPrev: false,
            isInteractive: true,
        }, () => liveAnnounce(l10n.formatText('gallerycarousel.imageChange', currentIndex + 1, images.length, captionToBeRead), Level.POLITE));
    }
    // Make allyHeadingLevel a valid tag if its type is 'number'
    // Defaults to h3 if number is not a valid level
    makeValidTag(level) {
        if (typeof level === 'number') {
            if (level < 1 || level > 6 || !Number.isInteger(level)) {
                return defaultHeadingTag;
            }
            return `h${level}`;
        }
        return level;
    }
    render() {
        const { allyHeadingLevel, allyHeadingText, analytics, buttonText, carouselName, carouselId = 'carousel-navigation-id', listingName, className, images, l10n, showGalleryButton, galleryButtonRef, showScrim, showVirtualTourButton, virtualTourButtonRef, imageTriggerRef, navTheme, ratio, videoAnalytics, showCaption, videoButtonText, placeholderImage, triggerA11yText, fetchPriority, } = this.props;
        const { currentIndex, isAnimating, isAnimatingNext, isAnimatingPrev, enablePreload } = this.state;
        let figureAnimationClass = '';
        const baseCssClass = getClassNameList$1('uitk-gallery-carousel', className);
        const imagesLength = images.length;
        const HeadingLevel = allyHeadingLevel
            ? EGDSHeadingTags.get(this.makeValidTag(allyHeadingLevel))
            : EGDSHeadingTags.get(defaultHeadingTag);
        if (isAnimating) {
            if (isAnimatingPrev) {
                figureAnimationClass = 'uitk-gallery-carousel-prev';
            }
            else if (isAnimatingNext) {
                figureAnimationClass = 'uitk-gallery-carousel-next';
            }
        }
        // Will send a track event with the current index when paginating backwards
        const clickLeftAnalytics = analytics && {
            localAnalyticsId: `${carouselName}.GalleryCarousel.${EGDSGalleryCarouselLocalAnalyticsIdType.clickLeft}.${currentIndex}`,
            ...analytics,
        };
        // Will send a track event with the current index when paginating forwards
        const clickRightAnalytics = analytics && {
            localAnalyticsId: `${carouselName}.GalleryCarousel.${EGDSGalleryCarouselLocalAnalyticsIdType.clickRight}.${currentIndex}`,
            ...analytics,
        };
        const clickMediaGalleryAnalytics = analytics && {
            localAnalyticsId: `${carouselName}.MediaGalleryTrigger`,
            ...analytics,
        };
        const buttonAndCaptionMarkup = (jsxs("div", { className: getClassNameList$1('uitk-gallery-carousel-footer'), children: [showCaption && (jsx("div", { className: getClassNameList$1('uitk-gallery-carousel-footer-media-caption'), children: images[currentIndex].caption })), jsxs("div", { className: getClassNameList$1('uitk-action-buttons'), children: [showVirtualTourButton && (jsxs(EGDSOverlayButton, { className: getClassNameList$1('uitk-virtual-tour-button'), analytics: clickMediaGalleryAnalytics, onClick: this.onVirtualTourCallback, domRef: virtualTourButtonRef, children: [jsx(EGDSIcon, { name: "tour_360", title: buttonText?.virtualTourButton, id: `tour_360-${carouselId}` }), jsx("span", { "aria-hidden": "true", children: buttonText?.virtualTourButton })] })), showGalleryButton && (jsxs(EGDSOverlayButton, { className: getClassNameList$1('uitk-gallery-button'), analytics: clickMediaGalleryAnalytics, onClick: this.onMediaButtonCallback, domRef: galleryButtonRef, children: [jsx(EGDSIcon, { name: "photo_library", title: buttonText?.galleryButton ??
                                        (listingName
                                            ? l10n.formatText('gallerycarousel.mediaGalleryButton.withName', imagesLength, listingName)
                                            : l10n.formatText('gallerycarousel.mediaGalleryButton', imagesLength)), id: `photo_library-${carouselId}` }), jsx("span", { "aria-hidden": "true", children: String(imagesLength) })] }))] })] }));
        return (images &&
            imagesLength > 0 && (jsxs("div", { className: baseCssClass, onTransitionEnd: this.stopTransitionPropagation, children: [jsx(HeadingLevel, { className: getClassNameList$1('is-visually-hidden'), children: allyHeadingText }), jsxs(Swipeable, { className: getClassNameList$1(`uitk-gallery-carousel-items`, figureAnimationClass, ratio), onSwiping: this.onSwiping, delta: 70, children: [createElement(EGDSGalleryCarouselItem, { ...images[this.getBackwardIndex(1)], ariaHidden: true, className: getClassNameList$1('uitk-gallery-carousel-item-prev'), ratio: ratio, enablePreload: enablePreload, key: `${carouselId}${this.currentIndex - 1}`, triggerRef: imageTriggerRef }), createElement(EGDSGalleryCarouselItem, { ...images[this.currentIndex], className: getClassNameList$1('uitk-gallery-carousel-item-current'), ratio: ratio, enablePreload: true, onClick: this.onImageClickCallback, videoAnalytics: videoAnalytics, videoButtonText: videoButtonText, placeholderImage: placeholderImage, triggerA11yText: triggerA11yText, key: `${carouselId}${this.currentIndex}`, fetchPriority: fetchPriority, triggerRef: imageTriggerRef }), createElement(EGDSGalleryCarouselItem, { ...images[this.getForwardIndex(1)], ariaHidden: true, className: getClassNameList$1('uitk-gallery-carousel-item-next'), ratio: ratio, enablePreload: enablePreload, key: `${carouselId}${this.currentIndex + 1}`, triggerRef: imageTriggerRef }), jsx(EGDSGalleryCarouselItem, { ...images[this.getForwardIndex(2)], ariaHidden: true, className: getClassNameList$1('uitk-gallery-carousel-item-queued'), ratio: ratio, enablePreload: enablePreload, triggerRef: imageTriggerRef })] }), imagesLength > 1 && (jsxs("div", { className: getClassNameList$1('uitk-gallery-carousel-paging-controls'), children: [jsx(EGDSPagingPrevButton, { className: getClassNameList$1('uitk-gallery-carousel-button-prev'), iconId: `prev-button-${carouselId}`, iconTitle: buttonText?.prevButton ? buttonText.prevButton : l10n.formatText('gallerycarousel.previousImage'), onClick: this.onPrevCallback, analytics: clickLeftAnalytics, theme: navTheme }), jsx(EGDSPagingNextButton, { className: getClassNameList$1('uitk-gallery-carousel-button-next'), iconId: `next-button-${carouselId}`, iconTitle: buttonText?.nextButton ? buttonText.nextButton : l10n.formatText('gallerycarousel.nextImage'), onClick: this.onNextCallback, analytics: clickRightAnalytics, theme: navTheme })] })), showScrim && (jsx(EGDSScrim, { className: getClassNameList$1('uitk-gallery-carousel-scrim'), tag: "div", type: EGDSScrimType.BOTTOM, children: (showCaption || showGalleryButton || showVirtualTourButton) && buttonAndCaptionMarkup })), !showScrim && (showCaption || showGalleryButton || showVirtualTourButton) && (jsx("div", { className: getClassNameList$1('uitk-gallery-carousel-box'), children: buttonAndCaptionMarkup }))] })));
    }
}
const EGDSGalleryCarousel = (props) => {
    const l10n = useLocalization();
    return jsx(GalleryCarousel, { l10n: l10n, ...props });
};

const getClassNameList = classNames.bind(styles);
class EGDSGalleryCarouselNavdots extends React.PureComponent {
    static displayName = 'EGDSGalleryCarouselNavdots';
    getdotStyles(currentIndex, imagesLengthNumber, padding) {
        // Patterns are a pattern of dots that should be shown depending on how many images there are.
        // the numbers corrolate to the index in styles array
        // ie. 02321 === dot-zero, dot-medium, dot-current, etc.
        const patterns = {
            // 5 === five or more images, 4 === four images, etc.
            5: ['03221', '02321', '12321', '12320', '12230'],
            4: ['3221', '2321', '1232', '1223'],
            3: ['322', '232', '223'],
            2: ['32', '23'],
        };
        const styles = ['dot-zero', 'dot-small', 'dot-medium', 'dot-current'];
        const { imagesLength } = this.props;
        const fiveOrMore = imagesLength >= 5;
        const patternLength = fiveOrMore ? 5 : imagesLength;
        let currentIndexNumber = currentIndex;
        if (fiveOrMore && currentIndexNumber >= 2 && currentIndexNumber < imagesLengthNumber - 2) {
            currentIndexNumber = 2;
        }
        else if (fiveOrMore && currentIndexNumber === imagesLengthNumber - 2) {
            currentIndexNumber = 3;
        }
        else if (fiveOrMore && currentIndexNumber === imagesLengthNumber - 1) {
            currentIndexNumber = 4;
        }
        const currentPattern = patterns[patternLength][currentIndexNumber].split('');
        // Push extra zeros so that there are dots to be grown or shrunk at the edges
        if (padding === 'left') {
            currentPattern.unshift('0', '0');
        }
        else if (padding === 'right') {
            currentPattern.push('0', '0');
        }
        else if (fiveOrMore) {
            currentPattern.push('0');
            currentPattern.unshift('0');
        }
        const dotStyles = currentPattern.map((x) => styles[x]);
        return dotStyles;
    }
    getDotList(currentIndex, imagesLength, padding) {
        let elementList = [];
        if (imagesLength > 1) {
            const styles = this.getdotStyles(currentIndex, imagesLength, padding);
            elementList = styles.map((x, index) => (jsx("div", { className: getClassNameList('uitk-gallery-carousel-dot-wrap'), children: jsx("div", { className: getClassNameList('uitk-gallery-carousel-dot', x) }) }, index)));
        }
        return elementList;
    }
    render() {
        const { animation, currentIndex, imagesLength } = this.props;
        if (imagesLength <= 1) {
            return null;
        }
        let animationstyles = animation;
        let dotsShown = this.getDotList(currentIndex, imagesLength);
        if (animation && animation !== '') {
            if (animation === 'uitk-gallery-carousel-next') {
                dotsShown = this.getDotList(currentIndex + 1, imagesLength);
            }
            if (animation === 'uitk-gallery-carousel-prev') {
                dotsShown = this.getDotList(currentIndex - 1, imagesLength);
            }
            if (animation === 'uitk-gallery-carousel-next' &&
                currentIndex >= 1 &&
                currentIndex < imagesLength - 2 &&
                imagesLength >= 5) {
                animationstyles += ' uitk-gallery-carousel-midpoint';
                dotsShown = this.getDotList(currentIndex + 1, imagesLength, 'left');
            }
            if (animation === 'uitk-gallery-carousel-prev' &&
                currentIndex > 1 &&
                currentIndex <= imagesLength - 2 &&
                imagesLength >= 5) {
                animationstyles += ' uitk-gallery-carousel-midpoint';
                dotsShown = this.getDotList(currentIndex - 1, imagesLength, 'right');
            }
        }
        return jsx("div", { className: getClassNameList(`uitk-gallery-carousel-navi-dots`, animationstyles), children: dotsShown });
    }
}

export { EGDSGalleryCarouselLocalAnalyticsIdType as E, EGDSGalleryCarousel as a, EGDSGalleryCarouselItem as b, EGDSGalleryCarouselNavdots as c };
//# sourceMappingURL=index-chunk.esm.js.map
